import { PageContainer } from "@/ui/containers/PageContainer";
import { Table, type TableProps } from "@/ui/tables/Table";
import { getStoredPageSize } from "@/ui/tables/pageSize";
import {
  Box,
  Button,
  HStack,
  Table as ChakraTable,
  Text,
} from "@chakra-ui/react";
import { LuExternalLink, LuFileSpreadsheet, LuPlus } from "react-icons/lu";
import { useEffect, useMemo, useState } from "react";
import { notify } from "@/ui/chakra/notify";
import { useConfirmToggle } from "@/ui/modals/useConfirm";
import type { CreateReposition, Reposition } from "../types/Replenishment";
import { FilterChips, type FilterChipOption } from "@/ui/tables/FilterChips";
import { withTotal } from "@/ui/tables/filterChipCounts";
import { TableFilters } from "@/ui/filters/TableFilters";
import {
  countActiveFilters,
  getDateRange,
  getOption,
  toDateRangeParams,
  type FilterField,
  type FilterValues,
} from "@/ui/filters/filterValues";
import { useStorageFilterField } from "@/core/storages/useCases/useStorageFilterField";
import {
  useGetRepositions,
  type InvoiceFilter,
  type RepositionFilters,
} from "../useCases/useGetReposition";
import { ModalReplenishment } from "../components/ModalReplenishment";
import { useUpdateReposition } from "../useCases/useUpdateReposition";
import { useCreateReposition } from "../useCases/useCreateReposition";
import ErrorStockAlert from "@/core/products/components/ErrorStockAlert";
import { Pagination } from "@/shared/core-configs";
import { replenishmentApi } from "@/shared/core-replenishments";
import { useDebounce } from "@/core/common/hooks/useDebounce";
import { useAuth } from "@/shared/session";
import { trackEvent } from "@/utils/firebase/analytics";
import { downloadRepositionsReport } from "../utils/downloadRepositionsReport";
import qr from "query-string";
import { toast } from "@/ui/chakra/toaster";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "@/ui/chakra/dialog";
import { CreatorCell } from "@/core/vouchers/Columns";

const headerProps = {
  fontSize: "xs",
  fontWeight: "semibold",
  letterSpacing: "wider",
  textTransform: "uppercase",
  color: "fg.muted",
  whiteSpace: "nowrap",
} as const;

const formatCLP = (value: number) =>
  new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    maximumFractionDigits: 0,
  }).format(value);

type InvoiceChip = "all" | InvoiceFilter;

const INVOICE_CHIPS: FilterChipOption<InvoiceChip>[] = [
  { value: "all", label: "Todas" },
  { value: "with", label: "Con factura" },
  { value: "without", label: "Sin factura" },
];

export const ReplenishmentsPage = () => {
  const { session } = useAuth();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewProductsModalOpen, setIsViewProductsModalOpen] = useState(false);

  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    pageSize: getStoredPageSize(),
  });

  const [errorRelatedVouchers, setErrorRelatedVouchers] = useState<
    | {
        voucherCode: string;
        usedStock: number;
        productCode: string;
      }[]
    | null
  >(null);
  const [selectedReposition, setSelectedReposition] =
    useState<Reposition | null>(null);
  const [showDisabled, setShowDisabled] = useState(false);

  const [order, setOrder] = useState<{
    sort?: string;
    order?: string;
  }>({
    sort: undefined,
    order: undefined,
  });

  const [searchTerm, setSearchTerm] = useState<string>();
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const storageField = useStorageFilterField();
  const filterFields = useMemo<FilterField[]>(
    () => [
      { type: "dateRange", key: "date", label: "Fecha de creación" },
      storageField,
    ],
    [storageField],
  );
  const [invoiceFilter, setInvoiceFilter] = useState<InvoiceChip>("all");
  const [extraFilters, setExtraFilters] = useState<FilterValues>({});
  const filters = useMemo<RepositionFilters>(
    () => ({
      ...toDateRangeParams(getDateRange(extraFilters, "date")),
      storageId: getOption(extraFilters, "storage")?.value,
      invoice: invoiceFilter === "all" ? undefined : invoiceFilter,
    }),
    [extraFilters, invoiceFilter],
  );
  const hasActiveFilters =
    invoiceFilter !== "all" || countActiveFilters(extraFilters) > 0;

  const {
    data: repositions,
    refetch,
    isPending,
    pagination: metaReplenishment,
  } = useGetRepositions({
    page: pagination.page,
    pageSize: pagination.pageSize,
    showDisabled,
    ...order,
    searchTerm: debouncedSearchTerm,
    zoneId: session?.zone ?? undefined,
    filters,
  });
  const { mutate: updateReposition } = useUpdateReposition();
  const { mutate: createReposition } = useCreateReposition();

  const handleCreateReposition = async (
    data: CreateReposition,
    invoiceFile?: File | null,
  ) => {
    if (!data.supplierRut || typeof data.supplierRut !== "string") {
      console.error("supplierRut is required");
      return;
    }

    createReposition(data, {
      onSuccess: async (data) => {
        trackEvent("replenishment_created", {
          replenishment_id: data.id,
        });
        refetch();
        if (invoiceFile) {
          await replenishmentApi.uploadInvoice(data.id, invoiceFile);
        }
        toast({
          title: "Reposición creada",
          status: "success",
          duration: 3000,
        });
        setIsCreateModalOpen(false);
      },
      onError: (error) => {
        console.error("Error al crear la reposición:", error);
      },
    });
  };

  const handleUpdateReposition = (
    reposition: CreateReposition,
    invoiceFile?: File | null,
  ) => {
    if (!selectedReposition) {
      console.error("selectedReposition is required");
      return;
    }
    updateReposition(
      { id: selectedReposition.id, data: reposition },
      {
        onSuccess: async (data) => {
          toast({
            title: "Reposición actualizada",
            status: "success",
            duration: 3000,
            isClosable: true,
          });
          if (invoiceFile) {
            await replenishmentApi.uploadInvoice(data.id, invoiceFile);
          }
          setIsEditModalOpen(false);
          setSelectedReposition(null);
        },
        onError: (error) => {
          setErrorRelatedVouchers(error.response?.data?.error?.relatedVouchers);
        },
      },
    );
  };

  const confirmToggle = useConfirmToggle();

  const handleToggleEnabled = async (reposition: Reposition) => {
    const ok = await confirmToggle(
      reposition.enabled,
      `la reposición de la factura ${reposition.invoiceNumber}`,
    );
    if (!ok) return;
    updateReposition(
      {
        id: reposition.id,
        data: { enabled: !reposition.enabled },
      },
      {
        onSuccess: () =>
          notify.success(
            reposition.enabled
              ? "Reposición deshabilitada"
              : "Reposición habilitada",
          ),
        onError: (error) => {
          const relatedVouchers = error.response?.data?.error?.relatedVouchers;
          if (relatedVouchers) {
            setErrorRelatedVouchers(relatedVouchers);
            return;
          }
          notify.error("No se pudo actualizar la reposición", error);
        },
      },
    );
  };

  const getColumns = (): TableProps<Reposition>["headers"] => [
    {
      label: "Número de factura",
      sortable: true,
      sortKey: "invoiceNumber",
      itemLabel: (item) => (
        <Text fontWeight="semibold">{item.invoiceNumber}</Text>
      ),
    },
    {
      label: "RUT del Proveedor",
      sortable: true,
      sortKey: "supplierRut",
      itemLabel: (item) => <span>{item.supplierRut}</span>,
    },
    {
      label: "Bodega",
      itemLabel: (item) => <span>{item.storage.name}</span>,
    },
    {
      label: "Factura",
      itemLabel: (item) =>
        item.invoiceUrl ? (
          <Button asChild size="sm" variant="outline">
            <a href={item.invoiceUrl} target="_blank" rel="noreferrer">
              Ver factura
              <LuExternalLink />
            </a>
          </Button>
        ) : (
          <Text color="fg.muted">---</Text>
        ),
    },
    {
      label: "Fecha de creación",
      sortable: true,
      sortKey: "createdAt",
      itemLabel: (item) => (
        <span>{new Date(item.createdAt).toLocaleDateString("es-CL")}</span>
      ),
    },
    {
      label: "Creado por",
      itemLabel: (item) => <CreatorCell name={item.creator?.name} />,
    },
    {
      label: "Productos",
      itemLabel: (item) => (
        <Button
          size="sm"
          variant="outline"
          onClick={() =>
            handleOpenViewProductsModal({
              ...item,
              products: Array.isArray(item.products) ? item.products : [],
            })
          }
        >
          Ver productos
        </Button>
      ),
    },
    {
      label: "Estado",
      itemLabel: (item) => (
        <Button
          variant="ghost"
          size="sm"
          colorPalette={item.enabled ? "gray" : "green"}
          color={item.enabled ? "fg.muted" : undefined}
          onClick={() => handleToggleEnabled(item)}
        >
          {item.enabled ? "Deshabilitar" : "Habilitar"}
        </Button>
      ),
    },
  ];

  const handleOpenCreateModal = () => {
    setSelectedReposition(null);
    setIsCreateModalOpen(true);
  };

  const handleOpenEditModal = (reposition: Reposition) => {
    setSelectedReposition(reposition);
    setIsEditModalOpen(true);
  };

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
  };

  const handleCloseEditModal = () => {
    setSelectedReposition(null);
    setIsEditModalOpen(false);
  };

  const handleOpenViewProductsModal = (reposition: Reposition) => {
    setSelectedReposition(reposition);
    setIsViewProductsModalOpen(true);
  };

  const handleCloseViewProductsModal = () => {
    setSelectedReposition(null);
    setIsViewProductsModalOpen(false);
  };

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [debouncedSearchTerm, filters, showDisabled]);

  const handleOnSortChange = (sort: string, order: string) => {
    setOrder({ sort, order });
  };

  const [exporting, setExporting] = useState(false);

  const handleExportExcel = async () => {
    const params = qr.stringify({
      zoneId: session?.zone,
      showDisabled,
      searchTerm: debouncedSearchTerm,
      sort: order.sort,
      order: order.order,
      ...filters,
    });
    setExporting(true);
    try {
      await notify.run(() => downloadRepositionsReport(params), {
        loading: "Preparando el Excel de reposiciones…",
        error: "No se pudo exportar a Excel",
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <PageContainer
      display="flex"
      flexDir="column"
      gap={4}
      title="Reposiciones de productos"
      actions={
        <>
          <Button colorPalette="brand" onClick={handleOpenCreateModal}>
            <LuPlus />
            Crear reposición
          </Button>
        </>
      }
    >
      <Table
        pageSize={pagination.pageSize}
        onChangePageSize={(pageSize) => setPagination({ page: 1, pageSize })}
        isLoading={isPending}
        page={pagination.page}
        hasActiveFilters={hasActiveFilters}
        onClearFilters={() => {
          setInvoiceFilter("all");
          setExtraFilters({});
        }}
        filters={
          <HStack gap={1.5} flexWrap="wrap">
            <FilterChips
              options={INVOICE_CHIPS}
              value={invoiceFilter}
              onChange={setInvoiceFilter}
              counts={withTotal(
                metaReplenishment?.countByInvoice,
                "with",
                "without",
              )}
            />
            <TableFilters
              fields={filterFields}
              value={extraFilters}
              onChange={setExtraFilters}
            />
          </HStack>
        }
        emptyDescription="Una reposición registra la factura de un proveedor y los productos que entran a bodega."
        emptyAction={
          <Button
            colorPalette="brand"
            size="sm"
            onClick={handleOpenCreateModal}
          >
            Crear reposición
          </Button>
        }
        data={repositions}
        headers={getColumns()}
        extractItemKey={(item) => item.id}
        pages={metaReplenishment?.totalPages}
        totalItems={metaReplenishment?.count}
        itemsLabel="reposiciones"
        searchPlaceholder="Buscar por número de factura o RUT…"
        onChangePage={(page) =>
          setPagination({ ...pagination, page: page + 1 })
        }
        showDisabledCheck
        onShowDisabledChange={(showDisabled) => setShowDisabled(showDisabled)}
        onSortChange={handleOnSortChange}
        headerActions={
          <Button
            size="sm"
            variant="outline"
            colorPalette="brand"
            onClick={handleExportExcel}
            loading={exporting}
          >
            <LuFileSpreadsheet />
            Exportar a Excel
          </Button>
        }
        hiddenColumnsKey="replenishments"
        onSearch={(search) => setSearchTerm(search)}
        onEdit={(item) => handleOpenEditModal(item)}
      />

      <ModalReplenishment
        isOpen={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        onSubmit={(data, invoiceFile) =>
          handleCreateReposition(data, invoiceFile)
        }
      />

      {selectedReposition && (
        <ModalReplenishment
          isOpen={isEditModalOpen}
          onClose={handleCloseEditModal}
          onSubmit={(data, invoiceFile) =>
            handleUpdateReposition(data, invoiceFile)
          }
          initialData={selectedReposition}
        />
      )}

      <DialogRoot
        isOpen={isViewProductsModalOpen}
        onClose={handleCloseViewProductsModal}
        size="xl"
        isCentered
      >
        <DialogContent maxW="5xl">
          <DialogHeader borderBottomWidth="1px" borderColor="border">
            <DialogTitle>
              Número de factura: {selectedReposition?.invoiceNumber}
            </DialogTitle>
          </DialogHeader>
          <DialogCloseTrigger />
          <DialogBody pt={4}>
            <Box borderWidth="1px" borderColor="border" rounded="lg">
              <ChakraTable.ScrollArea>
                <ChakraTable.Root size="sm" variant="line">
                  <ChakraTable.Header bg="bg.subtle">
                    <ChakraTable.Row>
                      <ChakraTable.ColumnHeader {...headerProps}>
                        ID Producto
                      </ChakraTable.ColumnHeader>
                      <ChakraTable.ColumnHeader {...headerProps}>
                        Descripción
                      </ChakraTable.ColumnHeader>
                      <ChakraTable.ColumnHeader
                        {...headerProps}
                        textAlign="end"
                      >
                        Stock
                      </ChakraTable.ColumnHeader>
                      <ChakraTable.ColumnHeader
                        {...headerProps}
                        textAlign="end"
                      >
                        Reposición
                      </ChakraTable.ColumnHeader>
                      <ChakraTable.ColumnHeader
                        {...headerProps}
                        textAlign="end"
                      >
                        Precio
                      </ChakraTable.ColumnHeader>
                    </ChakraTable.Row>
                  </ChakraTable.Header>
                  <ChakraTable.Body>
                    {selectedReposition?.products.map((product) => (
                      <ChakraTable.Row key={product.id}>
                        <ChakraTable.Cell
                          fontFamily="mono"
                          fontSize="xs"
                          maxW="160px"
                          truncate
                          title={product.id}
                        >
                          {product.id}
                        </ChakraTable.Cell>
                        <ChakraTable.Cell>
                          {product.description}
                        </ChakraTable.Cell>
                        <ChakraTable.Cell textAlign="end">
                          {product.stock}
                        </ChakraTable.Cell>
                        <ChakraTable.Cell textAlign="end" fontWeight="semibold">
                          {(
                            product.repositionProduct as {
                              stockIncoment: number;
                            }
                          )?.stockIncoment || 0}
                        </ChakraTable.Cell>
                        <ChakraTable.Cell textAlign="end">
                          {formatCLP(
                            (
                              product.repositionProduct as {
                                price: number;
                              }
                            )?.price || 0,
                          )}
                        </ChakraTable.Cell>
                      </ChakraTable.Row>
                    ))}
                    {selectedReposition?.products.length === 0 && (
                      <ChakraTable.Row>
                        <ChakraTable.Cell
                          colSpan={5}
                          textAlign="center"
                          py={8}
                          color="fg.muted"
                        >
                          No hay productos asignados a esta reposición.
                        </ChakraTable.Cell>
                      </ChakraTable.Row>
                    )}
                  </ChakraTable.Body>
                </ChakraTable.Root>
              </ChakraTable.ScrollArea>
            </Box>
          </DialogBody>
        </DialogContent>
      </DialogRoot>
      <ErrorStockAlert
        isOpen={!!errorRelatedVouchers}
        type="reposition"
        onClose={() => setErrorRelatedVouchers(null)}
        relatedVouchers={errorRelatedVouchers || []}
      />
    </PageContainer>
  );
};
